import { onUnmounted, ref } from '@vue/composition-api';

const DETAIL_IDLE = 'idle';
const DETAIL_LOADING = 'loading';
const DETAIL_LOADED = 'loaded';
const DETAIL_ERROR = 'error';

const LOCK_IDLE = 'idle';
const LOCK_LOCKING = 'locking';
const LOCK_OWNED = 'owned';
const LOCK_BLOCKED = 'blocked';
const LOCK_ERROR = 'error';

export function useCrisisFacilityDetails({ axios, props, notify }) {
  const detailStatus = ref({});
  const locks = ref({});
  const heartbeatTimers = {};

  const canEditAfterAcquireFailure = () => props.allowEditOnLockFailure === true;
  const getBranchKey = (branch) => String(branch?.id_unit);
  const getLockOwner = (lock) => lock?.lock_owner ?? lock?.owner ?? null;
  const getPayloadLockOwner = (payload) => payload?.lock_owner ?? payload?.owner ?? null;
  const isLockTokenExpired = (error) => error?.response?.status === 423;

  const updateDetailStatus = (branch, status) => {
    const key = getBranchKey(branch);
    detailStatus.value = { ...detailStatus.value, [key]: status };
  };

  const updateLock = (branch, value) => {
    const key = getBranchKey(branch);
    locks.value = {
      ...locks.value,
      [key]: {
        status: LOCK_IDLE,
        editable: false,
        owner: null,
        ...locks.value[key],
        ...value,
      },
    };
  };

  const fetchFacilityDetails = async (branch, force = false) => {
    const key = getBranchKey(branch);

    if (!force && detailStatus.value[key] === DETAIL_LOADED) {
      return null;
    }

    updateDetailStatus(branch, DETAIL_LOADING);

    try {
      const url = `/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/units/${branch.id_unit}`;
      const response = await axios.get(url);
      // Return raw data so the parent owns normalization and unit replacement.
      const detail = response.data?.data ?? response.data;

      if (detail?.lock) {
        const currentLock = locks.value[key];
        const owner = getLockOwner(detail.lock);
        const keepFallbackEdit = canEditAfterAcquireFailure() && currentLock?.status === LOCK_ERROR && currentLock.editable && !owner;

        if (keepFallbackEdit) {
          updateDetailStatus(branch, DETAIL_LOADED);
          return detail;
        }

        // Keep the UI aligned when the detail endpoint already reports lock state.
        updateLock(branch, {
          status: detail.lock.editable ? LOCK_OWNED : LOCK_BLOCKED,
          editable: Boolean(detail.lock.editable),
          owner,
          expires_at: detail.lock.expires_at ?? null,
        });
      }

      updateDetailStatus(branch, DETAIL_LOADED);
      return detail;
    } catch (error) {
      updateDetailStatus(branch, DETAIL_ERROR);
      notify('Error cargando detalle de sucursal', 'danger', 'AlertTriangleIcon');
      return null;
    }
  };

  const stopHeartbeat = (branch) => {
    const key = getBranchKey(branch);

    if (heartbeatTimers[key]) {
      clearInterval(heartbeatTimers[key]);
      delete heartbeatTimers[key];
    }
  };

  const acquireLock = async (branch, shouldAcquire) => {
    if (!shouldAcquire) {
      return;
    }

    updateLock(branch, { status: LOCK_LOCKING, editable: false });

    try {
      // Opening an editable facility asks the API for exclusive edit ownership.
      const url = `/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/units/${branch.id_unit}/lock`;
      const response = await axios.post(url);
      const lock = response.data?.data ?? response.data;
      const editable = Boolean(lock?.editable ?? lock?.locked);

      updateLock(branch, {
        status: editable ? LOCK_OWNED : LOCK_BLOCKED,
        editable,
        owner: lock?.lock_owner ?? lock?.owner ?? null,
        expires_at: lock?.expires_at ?? null,
      });

      if (editable) {
        // Only owned locks are renewed; blocked facilities stay read-only.
        startHeartbeat(branch);
      }
    } catch (error) {
      const lock = error?.response?.data?.data ?? error?.response?.data;
      const owner = getLockOwner(lock);
      const allowFallbackEdit = !owner && canEditAfterAcquireFailure();

      updateLock(branch, {
        status: owner ? LOCK_BLOCKED : LOCK_ERROR,
        editable: allowFallbackEdit,
        owner,
        expires_at: lock?.expires_at ?? null,
      });

      if (allowFallbackEdit) {
        notify('No fue posible bloquear la sucursal; la edición continuará sin bloqueo', 'warning', 'AlertTriangleIcon');
      }
    }
  };

  const refreshLock = async (branch) => {
    try {
      // Heartbeat extends the server-side lock while the accordion remains open.
      const url = `/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/units/${branch.id_unit}/lock`;
      const response = await axios.put(url);
      const lock = response.data?.data ?? response.data;

      updateLock(branch, {
        status: LOCK_OWNED,
        editable: true,
        owner: lock?.lock_owner ?? lock?.owner ?? locks.value[getBranchKey(branch)]?.owner ?? null,
        expires_at: lock?.expires_at ?? null,
      });
    } catch (error) {
      stopHeartbeat(branch);

      if (isLockTokenExpired(error)) {
        // Expired lock tokens no longer own the server lock.
        updateLock(branch, {
          status: LOCK_ERROR,
          editable: canEditAfterAcquireFailure(),
          owner: null,
          expires_at: null,
        });
        notify('El bloqueo expiró; la edición continuará sin bloqueo', 'warning', 'AlertTriangleIcon');
        return;
      }

      updateLock(branch, { status: LOCK_ERROR, editable: false });
      notify('No fue posible mantener el bloqueo de edición', 'warning', 'AlertTriangleIcon');
    }
  };

  const startHeartbeat = (branch) => {
    const key = getBranchKey(branch);

    stopHeartbeat(branch);
    // Restarting prevents duplicate intervals if a facility is reopened quickly.
    heartbeatTimers[key] = setInterval(() => {
      refreshLock(branch);
    }, 30 * 1000);
  };

  const releaseLock = async (branch) => {
    const key = getBranchKey(branch);
    const lock = locks.value[key];

    stopHeartbeat(branch);

    if (lock?.status !== LOCK_OWNED) {
      // There is no server-owned lock to release when the facility is blocked or idle.
      updateLock(branch, { status: LOCK_IDLE, editable: false });
      return;
    }

    try {
      // Closing the accordion releases the edit lock for other users.
      const url = `/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/units/${branch.id_unit}/lock`;
      await axios.delete(url);
    } finally {
      updateLock(branch, { status: LOCK_IDLE, editable: false });
    }
  };

  const applyRemoteLockAcquired = (branch, payload = {}) => {
    const currentLock = locks.value[getBranchKey(branch)];

    if (currentLock?.status === LOCK_OWNED) {
      return;
    }

    updateLock(branch, {
      status: LOCK_BLOCKED,
      editable: false,
      owner: getPayloadLockOwner(payload),
      expires_at: payload?.expires_at ?? null,
    });
  };

  const getLock = (branch) => locks.value[getBranchKey(branch)] ?? { status: LOCK_IDLE, editable: true };
  const getDetailStatus = (branch) => detailStatus.value[getBranchKey(branch)] ?? DETAIL_IDLE;

  const isFacilityReadOnly = (branch, baseReadOnly) => {
    if (baseReadOnly) {
      return true;
    }

    // Some lock failures can be configured to keep the form editable.
    return !getLock(branch).editable;
  };

  const releaseAllLocks = () => {
    Object.keys(heartbeatTimers).forEach((key) => {
      clearInterval(heartbeatTimers[key]);
      delete heartbeatTimers[key];
    });

    Object.keys(locks.value).forEach((key) => {
      const branch = props.operationalSecurityCrisis.units.find((unit) => getBranchKey(unit) === key);

      if (branch && locks.value[key]?.status === LOCK_OWNED) {
        releaseLock(branch);
      }
    });
  };

  onUnmounted(() => {
    // Component teardown must not leave heartbeat timers or owned locks active.
    releaseAllLocks();
  });

  return {
    DETAIL_ERROR,
    DETAIL_LOADED,
    DETAIL_LOADING,
    LOCK_BLOCKED,
    LOCK_ERROR,
    LOCK_LOCKING,
    LOCK_OWNED,
    acquireLock,
    applyRemoteLockAcquired,
    fetchFacilityDetails,
    getBranchKey,
    getDetailStatus,
    getLock,
    isFacilityReadOnly,
    releaseLock,
  };
}
